import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';
import { saveAudioBlob, loadAudioBlob, clearAudioBlob } from './audioStorage';
import { appendWithSessionDivider } from '../features/session-divider';
import { persistProfilePref } from '../utils/persistProfilePref';

const API = window.__WR_API_BASE__ || '';

// Constants - outside hook to avoid dependency issues
const MAX_FILE_SIZE = 24 * 1024 * 1024;
const WARNING_SIZE_GENTLE = 12 * 1024 * 1024; // Warn early — proxy limit is ~15MB
const WARNING_SIZE_MEDIUM = 14 * 1024 * 1024;

/**
 * useRecording - Hook for audio recording, transcription, and related functionality
 * Extracted from App.js during refactor (March 8, 2026)
 */
export const useRecording = ({ notes, setNotes }) => {
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingSize, setRecordingSize] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [useLiveTranscription, setUseLiveTranscription] = useState(false);
  const [quickNotes, setQuickNotes] = useState('');
  const [hasPlayed21MBWarning, setHasPlayed21MBWarning] = useState(false);
  const [hasPlayed23MBWarning, setHasPlayed23MBWarning] = useState(false);
  const hasPlayed21MBRef = useRef(false);
  const hasPlayed23MBRef = useRef(false);
  const snoozedRef = useRef(false);
  const reminderIntervalRef = useRef(null);
  const timeAlertSnoozedRef = useRef(false);
  const timeReminderIntervalRef = useRef(null);
  const timeAlertFiredRef = useRef({ gentle: false, urgent: false });
  const [audioLevel, setAudioLevel] = useState(0);
  const [noAudioWarningShown, setNoAudioWarningShown] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [lastFailedAudio, setLastFailedAudio] = useState(null);
  const [transcriptionError, setTranscriptionError] = useState(null);

  // Last-transcription timestamp (Feb 26, 2026). Used to decide whether to
  // prepend a `--- ENCOUNTER (HH:MM) ---` divider to a new dictation session.
  // Rule: only prepend if (a) prev content exists AND (b) it has been >25s
  // since the last transcription finished — that's a deliberate stop+restart,
  // not a mid-flight reconnect.
  const lastTranscriptionAtRef = useRef(0);

  // Recording Mode Selector (Feb 26, 2026) — controls Deepgram diarization.
  //   "solo"      → single dictation, no speaker labels (default)
  //   "in_person" → in-room encounter, Provider/Speaker labels
  //   "phone"     → phone call, Provider/Patient turn-by-turn 50/50
  // Persisted to localStorage so the provider's preferred mode survives reloads.
  const [recordingMode, setRecordingModeState] = useState(() => {
    try {
      const saved = localStorage.getItem('writeright_recording_mode');
      if (saved === 'solo' || saved === 'in_person' || saved === 'phone') return saved;
    } catch (e) { /* localStorage blocked */ }
    return 'solo';
  });
  const recordingModeRef = useRef(recordingMode);
  useEffect(() => { recordingModeRef.current = recordingMode; }, [recordingMode]);
  const setRecordingMode = useCallback((m) => {
    const next = (m === 'solo' || m === 'in_person' || m === 'phone') ? m : 'solo';
    setRecordingModeState(next);
    try { localStorage.setItem('writeright_recording_mode', next); } catch (e) { /* ignore */ }
    // Feb 28, 2026 — server-persist so Deepgram diarization preference
    // follows the user across devices/logins.
    persistProfilePref({ recording_mode: next });
  }, []);
  
  // Retry tracking for Sentry
  const [retryCount, setRetryCount] = useState(0);
  const [sessionTranscriptionAttempts, setSessionTranscriptionAttempts] = useState(0);

  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const cursorPositionRef = useRef({ start: 0, end: 0 });
  const recordingIntervalRef = useRef(null);
  const speechRecognitionRef = useRef(null);
  const audioAnalyserRef = useRef(null);
  const audioLevelIntervalRef = useRef(null);
  const notesTextareaRef = useRef(null);
  const isRecordingRef = useRef(false); // Ref version for closures
  const transcribeChunkedRef = useRef(null); // Stable ref for auto-transcription on stop
  const notesBeforeRecordingRef = useRef(''); // Track pre-recording notes to avoid duplication
  const browserSRCharCountRef = useRef(0); // Count chars added by browser SpeechRecognition

  // Screen Wake Lock — Feb 25, 2026 (revised again Feb 26, 2026).
  // Provider reported screen still locks mid-dictation despite Wake Lock API
  // wiring. Most likely cause: iOS Safari + non-user-gesture acquisition
  // silently fails, OR Wake Lock dropped on low-power mode. Defensive fix:
  // 1) Try Wake Lock API first.
  // 2) Always ALSO start a silent looping video element (NoSleep-style)
  //    that iOS counts as "active media playback" — keeps screen awake on
  //    every browser regardless of Wake Lock API support.
  // 3) Expose `screenLockActive` so the UI can show a confirmation chip.
  const wakeLockRef = useRef(null);
  const noSleepVideoRef = useRef(null);
  const [screenLockActive, setScreenLockActive] = useState(false);

  useEffect(() => {
    const isActive = isRecording || isTranscribing;
    let cancelled = false;

    const startNoSleepVideo = () => {
      try {
        if (noSleepVideoRef.current) return;
        const v = document.createElement('video');
        v.setAttribute('muted', '');
        v.setAttribute('playsinline', '');
        v.setAttribute('webkit-playsinline', '');
        v.muted = true;
        v.loop = true;
        // Tiny 320x180 mp4 stream — base64-encoded silent 1-second clip.
        // Inline base64 keeps it self-contained (no extra HTTP request).
        v.src = 'data:video/mp4;base64,AAAAHGZ0eXBpc29tAAACAGlzb21pc28ybXA0MQAAAAhmcmVlAAAAGm1kYXTeBAAAbGliZmFhYyAxLjI4AABCAJMgBDIARwAAArEm1vb3YAAABsbXZoZAAAAAB8JbCAfCWwgAAAA+gAAAAAAAEAAAEAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAQAAAAACEHRyYWsAAABcdGtoZAAAAA98JbCAfCWwgAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAAEAAAABAAAAAAAkZWR0cwAAABxlbHN0AAAAAAAAAAEAAAAAAAAAAAABAAAAACBdtZGlhAAAAIBtZGhkAAAAAHwlsIB8JbCAAACWADoAAAAAAAAxaGRscgAAAAAAAAAAdmlkZQAAAAAAAAAAAAAAAFZpZGVvSGFuZGxlcgAAAAFNbWluZgAAABR2bWhkAAAAAQAAAAAAAAAAAAAAJGRpbmYAAAAcZHJlZgAAAAAAAAABAAAADHVybCAAAAABAAABEXN0YmwAAACpc3RzZAAAAAAAAAABAAAAmWF2YzEAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAACAAH0AEgAAABIAAAAAAAAAAEUTGF2YzU2LjY0LjAyAAAAAAAAAAAAAAAYAACWAAAEgAAAAAAYP/4AAAAyYXZjQwHkAAr/4QAVZ+QACq7TIByAAGgGECAQENBhAQEEAaAAfAQAUaCAaCAcGwQQYIIAfFCEEMQQ8VgCgAQAAxgAAANC';
        // Hidden offscreen
        v.style.position = 'absolute';
        v.style.opacity = '0.001';
        v.style.width = '2px';
        v.style.height = '2px';
        v.style.left = '-9999px';
        document.body.appendChild(v);
        const p = v.play();
        if (p && p.catch) p.catch(() => { /* ignore autoplay block */ });
        noSleepVideoRef.current = v;
        console.log('[ScreenLock] no-sleep video started');
      } catch (e) {
        console.warn('[ScreenLock] no-sleep video failed:', e?.message || e);
      }
    };

    const stopNoSleepVideo = () => {
      try {
        if (noSleepVideoRef.current) {
          noSleepVideoRef.current.pause();
          noSleepVideoRef.current.remove();
          noSleepVideoRef.current = null;
          console.log('[ScreenLock] no-sleep video stopped');
        }
      } catch { /* ignore */ }
    };

    (async () => {
      if (isActive) {
        // Layer 1: Wake Lock API (works on Chrome, modern Android, Edge, iOS 16.4+)
        try {
          if ('wakeLock' in navigator && !wakeLockRef.current) {
            wakeLockRef.current = await navigator.wakeLock.request('screen');
            wakeLockRef.current.addEventListener('release', () => {
              wakeLockRef.current = null;
            });
            console.log('[ScreenLock] WakeLock API acquired');
          }
        } catch (err) {
          console.warn('[ScreenLock] WakeLock API failed (will rely on no-sleep video):', err?.message || err);
        }
        // Layer 2: ALWAYS start the no-sleep video too — defensive belt &
        // suspenders. iOS Safari + low-power mode silently drop Wake Lock.
        startNoSleepVideo();
        setScreenLockActive(true);
      } else {
        // Stop both.
        if (wakeLockRef.current) {
          try { await wakeLockRef.current.release(); } catch { /* ignore */ }
          wakeLockRef.current = null;
          console.log('[ScreenLock] WakeLock API released');
        }
        stopNoSleepVideo();
        setScreenLockActive(false);
      }
    })();

    const onVisibility = async () => {
      if (cancelled) return;
      if (!document.hidden && (isRecording || isTranscribing)) {
        if (!wakeLockRef.current && 'wakeLock' in navigator) {
          try { wakeLockRef.current = await navigator.wakeLock.request('screen'); }
          catch { /* ignore */ }
        }
        // Re-poke the video if iOS paused it on visibility flip.
        if (noSleepVideoRef.current) {
          try {
            const p = noSleepVideoRef.current.play();
            if (p && p.catch) p.catch(() => { /* ignore */ });
          } catch { /* ignore */ }
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isRecording, isTranscribing]);

  // Warn before leaving page while recording is active
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isRecordingRef.current || isTranscribing) {
        e.preventDefault();
        e.returnValue = 'Recording in progress — leaving will lose your audio. Stay on page?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isTranscribing]);

  // Feb 28, 2026 — Android Chrome mobile fix (provider report: "phone loses
  // transcription"). Two distinct failure modes were observed:
  //
  //  Mode A: Browser SpeechRecognition emits FINAL events less frequently
  //          on mobile (especially on long phrases). Interim text shows on
  //          screen via `liveTranscript` but is never committed to `notes`
  //          state. When the user locks the phone or switches tabs, Android
  //          suspends the page; if the OS later kills the tab for memory,
  //          the interim text is lost on reopen.
  //
  //  Mode B: Long uploads (11+ min recordings, multi-MB) can get killed
  //          mid-flight if the user backgrounds the tab during the POST.
  //          Recovery already exists via IndexedDB + `lastFailedAudio`, but
  //          users on mobile didn't notice the small "Enhance with AI"
  //          button. We surface a more prominent banner in NotesInput.
  //
  // This effect handles Mode A: commit liveTranscript to notes whenever the
  // tab is about to be suspended. Even partial commit is better than total
  // loss — the useAutosave hook will then persist the merged text within 5s.
  useEffect(() => {
    if (!isRecording) return;
    const commitInterim = () => {
      const interim = (typeof window !== 'undefined' && window.__lastLiveTranscript) || '';
      if (!interim) return;
      // Append interim to notes (matches the FINAL commit behavior in the
      // SpeechRecognition.onresult handler — line ~1080).
      setNotes((prev) => {
        // Avoid duplicate commits if the same interim was already committed.
        if (prev.endsWith(interim)) return prev;
        return prev + ' ' + interim;
      });
      // Reset the live transcript display — the text is now in `notes`.
      setLiveTranscript('');
      try { window.__lastLiveTranscript = ''; } catch { /* ignore */ }
    };
    const onVisibility = () => {
      if (document.hidden) commitInterim();
    };
    document.addEventListener('visibilitychange', onVisibility);
    // Also commit on pagehide (iOS Safari + Android Chrome fire this on
    // tab kill / app switch, even when visibilitychange doesn't).
    window.addEventListener('pagehide', commitInterim);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', commitInterim);
    };
  }, [isRecording, setNotes]);

  // Recover audio blob from IndexedDB on mount (survives deploys/refreshes)
  useEffect(() => {
    const recoverAudio = async () => {
      try {
        const saved = await loadAudioBlob();
        if (saved && saved.blob) {
          // Feb 28, 2026 — zombie-blob guard. Mirror backend MIN_BYTES=1024
          // (routes/transcribe.py L58). If the persisted blob is too small
          // to ever transcribe, purge it and DO NOT surface the "Transcribe
          // Now" banner — otherwise the user gets stuck in a loop of
          // 400 → "Network error" → retry → 400.
          const MIN_VALID_AUDIO_BYTES = 1024;
          if (saved.blob.size < MIN_VALID_AUDIO_BYTES) {
            clearAudioBlob().catch(() => {});
            return;
          }
          setLastFailedAudio(saved.blob);
          toast.info('Recovered recording from before the page refreshed. Click "Enhance with AI" to transcribe.', { duration: 8000 });
        }
      } catch (e) {
        // silent — IndexedDB may not be available
      }
    };
    recoverAudio();
  }, []);

  // Audio chimes
  const playGentleChime = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.setValueAtTime(660, audioContext.currentTime);
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      console.log('Could not play gentle chime:', e);
    }
  }, []);

  const playMediumWarningChime = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(1100, audioContext.currentTime + 0.15);
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime + 0.3);
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (e) {
      console.log('Could not play warning sound:', e);
    }
  }, []);

  const playAutoStopAlert = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.setValueAtTime(1000, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.15);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.3);
      gainNode.gain.setValueAtTime(0.35, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (e) {
      console.log('Could not play auto-stop alert:', e);
    }
  }, []);

  // Snooze recording size warning — silences reminders until next threshold
  const snoozeWarning = useCallback(() => {
    snoozedRef.current = true;
    if (reminderIntervalRef.current) {
      clearInterval(reminderIntervalRef.current);
      reminderIntervalRef.current = null;
    }
    toast.dismiss('size-warning');
    toast.success('Warning snoozed. Next alert at higher threshold.', { duration: 3000 });
  }, []);

  // Start gentle reminder beeps every 30 seconds until snoozed
  const startReminderBeeps = useCallback((level) => {
    if (reminderIntervalRef.current) clearInterval(reminderIntervalRef.current);
    snoozedRef.current = false;
    
    reminderIntervalRef.current = setInterval(() => {
      if (snoozedRef.current || !isRecordingRef.current) {
        clearInterval(reminderIntervalRef.current);
        reminderIntervalRef.current = null;
        return;
      }
      // Gentle single beep every 30 seconds
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.setValueAtTime(level === 'gentle' ? 660 : 880, audioContext.currentTime);
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.2);
      } catch (e) { console.warn('Reminder beep audio context error:', e.message); }
    }, 30000); // Every 30 seconds
  }, []);

  // Time-based appointment alert — snooze
  const snoozeTimeAlert = useCallback(() => {
    timeAlertSnoozedRef.current = true;
    if (timeReminderIntervalRef.current) {
      clearInterval(timeReminderIntervalRef.current);
      timeReminderIntervalRef.current = null;
    }
    toast.dismiss('time-warning');
    toast.success('Timer snoozed. Will remind again at 15 min.', { duration: 3000 });
  }, []);

  // Time-based appointment alert — start beeping
  const startTimeReminderBeeps = useCallback((level) => {
    if (timeReminderIntervalRef.current) clearInterval(timeReminderIntervalRef.current);
    timeAlertSnoozedRef.current = false;
    const interval = level === 'urgent' ? 15000 : 30000; // 15s urgent, 30s gentle
    timeReminderIntervalRef.current = setInterval(() => {
      if (timeAlertSnoozedRef.current || !isRecordingRef.current) {
        clearInterval(timeReminderIntervalRef.current);
        timeReminderIntervalRef.current = null;
        return;
      }
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        osc.connect(gain);
        gain.connect(audioContext.destination);
        osc.frequency.setValueAtTime(level === 'urgent' ? 1000 : 780, audioContext.currentTime);
        osc.type = 'sine';
        gain.gain.setValueAtTime(level === 'urgent' ? 0.25 : 0.15, audioContext.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);
        osc.start(audioContext.currentTime);
        osc.stop(audioContext.currentTime + 0.25);
      } catch (e) { /* silent */ }
    }, interval);
  }, []);

  // Cleanup transcription - remove garbage patterns from Whisper
  const cleanupTranscription = useCallback((text) => {
    if (!text) return text;
    let cleaned = text;
    cleaned = cleaned.replace(/(\b[A-Za-z]\b[\s.]+){5,}/g, ' ');
    cleaned = cleaned.replace(/([A-Za-z]\.){5,}/g, ' ');
    cleaned = cleaned.replace(/\b([a-zA-Z])\s+(\1\s+){4,}/gi, ' ');
    cleaned = cleaned.replace(/\b(uhh?|tdd?|hmm?|umm?)\s*/gi, '');
    cleaned = cleaned.replace(/[\s.]*([A-Za-z][\s.]+){10,}$/g, '');
    cleaned = cleaned.replace(/([a-zA-Z])\1{4,}/g, '');
    cleaned = cleaned.replace(/\s{2,}/g, ' ');
    cleaned = cleaned.replace(/\.{2,}/g, '.');
    cleaned = cleaned.trim();
    if (text.length - cleaned.length > 100) {
      toast.info('Cleaned up some transcription noise', { duration: 2000 });
    }
    return cleaned;
  }, []);

  // Detect Whisper hallucinations - common patterns when audio is unclear
  const isHallucination = useCallback((text) => {
    if (!text) return true;
    const lower = text.toLowerCase().trim();
    
    // Known Whisper hallucination patterns
    const hallucinations = [
      /^(thank you\.?\s*)+$/i,
      /^(thanks for watching\.?\s*)+$/i,
      /^(please subscribe\.?\s*)+$/i,
      /^(see you next time\.?\s*)+$/i,
      /^(bye\.?\s*)+$/i,
      /^(goodbye\.?\s*)+$/i,
      /^(hello\.?\s*)+$/i,
      /^(music\.?\s*)+$/i,
      /^\[music\]$/i,
      /^(applause\.?\s*)+$/i,
      /^\s*\.+\s*$/,  // Just periods
      /^[\s.,-]+$/,   // Just punctuation
    ];
    
    for (const pattern of hallucinations) {
      if (pattern.test(lower)) {
        return true;
      }
    }
    
    // Check for excessive repetition (same word/phrase repeated 3+ times)
    const words = lower.split(/\s+/);
    if (words.length >= 3) {
      const wordCounts = {};
      for (const word of words) {
        if (word.length > 2) {
          wordCounts[word] = (wordCounts[word] || 0) + 1;
        }
      }
      const maxCount = Math.max(...Object.values(wordCounts), 0);
      if (maxCount >= 3 && maxCount / words.length > 0.5) {
        return true; // More than half the text is one word repeated
      }
    }
    
    // Too short to be real speech (unless it's intentionally short)
    if (lower.length < 5 && !['yes', 'no', 'ok', 'okay'].includes(lower)) {
      return true;
    }
    
    return false;
  }, []);

  // Transcribe audio blob with automatic retry
  const transcribeAudio = useCallback(async (audioBlob, isManualRetry = false, autoRetryAttempt = 0) => {
    const MAX_AUTO_RETRIES = 3;
    const RETRY_DELAYS = [2000, 4000, 8000]; // Exponential backoff: 2s, 4s, 8s
    
    setIsTranscribing(true);
    setTranscriptionError(null);
    
    // Track attempt count
    const attemptNum = sessionTranscriptionAttempts + 1;
    setSessionTranscriptionAttempts(attemptNum);
    
    if (isManualRetry || autoRetryAttempt > 0) {
      const newRetryCount = retryCount + 1;
      setRetryCount(newRetryCount);
      
      if (autoRetryAttempt > 0) {
        toast.info(`Auto-retrying... (attempt ${autoRetryAttempt + 1}/${MAX_AUTO_RETRIES + 1})`, { duration: 2000 });
      }
      
      console.log(`[Transcription] Retry attempt #${newRetryCount} (auto: ${autoRetryAttempt})`);
      
      Sentry.addBreadcrumb({
        category: 'transcription',
        message: `Retry attempt #${newRetryCount}`,
        level: 'info',
        data: { 
          retry_count: newRetryCount,
          total_attempts: attemptNum,
          auto_retry_attempt: autoRetryAttempt,
          audio_size_mb: audioBlob ? (audioBlob.size / (1024 * 1024)).toFixed(2) : 'unknown'
        }
      });
    }
    
    try {
      const formData = new FormData();
      let extension = 'webm';
      if (audioBlob.type.includes('mp4')) extension = 'mp4';
      else if (audioBlob.type.includes('ogg')) extension = 'ogg';
      else if (audioBlob.type.includes('wav')) extension = 'wav';
      formData.append('file', audioBlob, `recording.${extension}`);
      formData.append('recording_mode', recordingModeRef.current || 'solo');
      
      const response = await axios.post(`${API}/api/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000  // 3 minutes for longer recordings
      });
      
      let transcribedText = response.data.text;
      if (!transcribedText || transcribedText.trim().length < 2) {
        // No speech detected - don't auto-retry for this, user needs to re-record
        setLastFailedAudio(audioBlob);
        setTranscriptionError('No speech detected. Please try again and speak clearly.');
        toast.error('No speech detected. Please record again and speak clearly.', { duration: 5000 });
        setIsTranscribing(false);
        
        Sentry.captureMessage('Transcription returned no speech', {
          level: 'warning',
          tags: { feature: 'transcription', issue: 'no_speech' },
          extra: { 
            attempt_number: attemptNum,
            retry_count: retryCount,
            audio_size_mb: (audioBlob.size / (1024 * 1024)).toFixed(2)
          }
        });
        return;
      }
      
      // Check for Whisper hallucinations (common when audio is unclear)
      if (isHallucination(transcribedText)) {
        console.warn('Hallucination detected:', transcribedText);
        setLastFailedAudio(audioBlob);
        setTranscriptionError('Audio unclear - transcription may be incorrect. Please check your microphone and try again.');
        toast.warning('⚠️ Audio may be unclear. Check mic and try again, or speak closer to device.', { duration: 6000 });
        return;
      }
      
      transcribedText = cleanupTranscription(transcribedText);
      
      // Always append transcription to the END of existing notes
      // (Inserting at stale cursor positions causes text to appear at top)
      // Feb 26, 2026 — new dictation session (gap >25s) gets a timestamped
      // divider so prep vs encounter vs follow-up are visually distinct.
      const now = Date.now();
      const lastAt = lastTranscriptionAtRef.current;
      lastTranscriptionAtRef.current = now;
      setNotes(prev => appendWithSessionDivider({ prev, transcribedText, now, lastAt }));
      
      setLastFailedAudio(null);
      setTranscriptionError(null);
      clearAudioBlob().catch(() => {}); // Clear IndexedDB backup after success
      
      // Log success with retry count if there were retries
      if (retryCount > 0) {
        Sentry.captureMessage('Transcription succeeded after retries', {
          level: 'info',
          tags: { feature: 'transcription', outcome: 'success_after_retry' },
          extra: { 
            retry_count: retryCount,
            total_attempts: sessionTranscriptionAttempts,
            audio_size_mb: (audioBlob.size / (1024 * 1024)).toFixed(2)
          }
        });
        // Reset retry count after success
        setRetryCount(0);
      }
      
      if (quickNotes) {
        setTimeout(() => {
          setNotes(prev => prev + '\n\n--- Quick Notes ---\n' + quickNotes);
          setQuickNotes('');
        }, 100);
      }
      
      toast.success('Transcription complete!' + (quickNotes ? ' Quick notes added.' : ''));
      
      // Safety net: backup transcribed notes to localStorage
      setTimeout(() => {
        try {
          const currentNotes = document.querySelector('[data-testid="notes-textarea"]')?.value || '';
          if (currentNotes.trim()) {
            localStorage.setItem('writeright_autosave', JSON.stringify({ notes: currentNotes, savedAt: new Date().toISOString(), source: 'transcription_success' }));
          }
        } catch (e) { /* silent */ }
      }, 200);
    } catch (error) {
      console.error('Transcription error:', error);
      
      // AUTO-RETRY LOGIC: Try up to 3 times before showing error
      const shouldAutoRetry = autoRetryAttempt < MAX_AUTO_RETRIES && 
        // Don't auto-retry for certain errors
        error.response?.status !== 400 && // Bad audio format
        !error.message?.includes('No speech');
      
      if (shouldAutoRetry) {
        const delay = RETRY_DELAYS[autoRetryAttempt] || 2000;
        console.log(`[Transcription] Auto-retrying in ${delay}ms (attempt ${autoRetryAttempt + 2}/${MAX_AUTO_RETRIES + 1})`);
        toast.info(`Retrying automatically... (${autoRetryAttempt + 2}/${MAX_AUTO_RETRIES + 1})`, { duration: delay });
        
        // Wait and retry
        await new Promise(resolve => setTimeout(resolve, delay));
        setIsTranscribing(false); // Reset before retry
        return transcribeAudio(audioBlob, false, autoRetryAttempt + 1);
      }
      
      // All retries exhausted - show error and offer manual retry / Enhance with AI
      setLastFailedAudio(audioBlob);
      
      // Report to Sentry with context (no PHI)
      Sentry.captureException(error, {
        tags: {
          feature: 'transcription',
          audio_size_mb: audioBlob ? (audioBlob.size / (1024 * 1024)).toFixed(2) : 'unknown',
          audio_type: audioBlob?.type || 'unknown',
          auto_retries_exhausted: 'yes'
        },
        extra: {
          error_code: error.code,
          response_status: error.response?.status,
          response_detail: error.response?.data?.detail,
          retry_count: retryCount,
          auto_retry_attempts: autoRetryAttempt,
          total_attempts: sessionTranscriptionAttempts
        }
      });
      
      let errorMsg = `Transcription failed after ${autoRetryAttempt + 1} attempts.`;
      let errorDetail = 'Click "Enhance with AI" to try the backup service.';
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMsg = 'Transcription timed out after multiple attempts.';
        errorDetail = 'Try "Enhance with AI" or record a shorter segment.';
      } else if (error.response?.status === 429) {
        errorMsg = 'Service is busy. Too many requests.';
        errorDetail = 'Wait a moment and click "Enhance with AI" to try again.';
      } else if (error.response?.status === 400) {
        errorMsg = error.response.data?.detail || 'Audio format issue.';
        errorDetail = 'Check microphone permissions and try recording again.';
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.message?.includes('Network')) {
        errorMsg = 'Network error after multiple attempts.';
        errorDetail = 'Check your connection and click "Enhance with AI".';
      }
      
      setTranscriptionError(`${errorMsg} ${errorDetail}`);
      toast.error(errorMsg, { 
        duration: 8000,
        description: errorDetail 
      });
    } finally {
      setIsTranscribing(false);
    }
  }, [cleanupTranscription, isHallucination, quickNotes, setNotes, retryCount, sessionTranscriptionAttempts]);

  // WAV encoder helper — creates a valid WAV from an AudioBuffer segment
  const audioBufferToWav = useCallback((buffer) => {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;
    const dataLength = buffer.length * blockAlign;
    const headerLength = 44;
    const totalLength = headerLength + dataLength;
    const arrayBuffer = new ArrayBuffer(totalLength);
    const view = new DataView(arrayBuffer);
    const writeString = (offset, str) => { for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i)); };
    writeString(0, 'RIFF'); view.setUint32(4, totalLength - 8, true); writeString(8, 'WAVE');
    writeString(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true); view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true); view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true); writeString(36, 'data'); view.setUint32(40, dataLength, true);
    let offset = 44;
    for (let i = 0; i < buffer.length; i++) {
      for (let ch = 0; ch < numChannels; ch++) {
        const sample = Math.max(-1, Math.min(1, buffer.getChannelData(ch)[i]));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
        offset += 2;
      }
    }
    return new Blob([arrayBuffer], { type: 'audio/wav' });
  }, []);

  // Transcription — sends full blob for files <20MB, proper time-based segments for larger
  const transcribeAudioChunked = useCallback(async (audioBlob, isRetry = false) => {
    setIsTranscribing(true);
    setTranscriptionError(null);
    
    try {
      const sizeMB = (audioBlob.size / (1024 * 1024)).toFixed(1);
      
      // For files under 20MB: send the whole blob (avoid header-stripping bug from byte slicing)
      if (audioBlob.size < 20 * 1024 * 1024) {
        toast.info(`Processing audio (${sizeMB}MB)...`, { duration: 4000 });
        const formData = new FormData();
        let extension = 'webm';
        if (audioBlob.type.includes('mp4')) extension = 'mp4';
        else if (audioBlob.type.includes('ogg')) extension = 'ogg';
        else if (audioBlob.type.includes('wav')) extension = 'wav';
        formData.append('file', audioBlob, `recording.${extension}`);
        formData.append('recording_mode', recordingModeRef.current || 'solo');
        
        let lastError = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            if (attempt > 0) {
              const delay = 3000 * Math.pow(2, attempt - 1);
              toast.info(`Retry ${attempt + 1}/3...`, { duration: delay });
              await new Promise(r => setTimeout(r, delay));
            }
            const response = await axios.post(`${API}/api/transcribe`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 180000,
            });
            
            if (response.data.text && !isHallucination(response.data.text)) {
              const text = cleanupTranscription(response.data.text);
              const now = Date.now();
              const lastAt = lastTranscriptionAtRef.current;
              lastTranscriptionAtRef.current = now;
              setNotes(prev => appendWithSessionDivider({ prev, transcribedText: text, now, lastAt }));
              setLastFailedAudio(null);
              setTranscriptionError(null);
              clearAudioBlob().catch(() => {});
              toast.success('Transcription complete!' + (quickNotes ? ' Quick notes added.' : ''));
              setTimeout(() => {
                try {
                  const el = document.querySelector('[data-testid="notes-textarea"]');
                  if (el?.value?.trim()) localStorage.setItem('writeright_autosave', JSON.stringify({ notes: el.value, savedAt: new Date().toISOString(), source: 'transcription' }));
                } catch (e) { /* silent */ }
              }, 200);
              setIsTranscribing(false);
              return;
            }
          } catch (err) {
            lastError = err;
            console.error(`Transcription attempt ${attempt + 1}:`, err.message);
          }
        }
        
        // All retries failed — keep existing notes, just report error
        setLastFailedAudio(audioBlob);
        // Feb 28, 2026 — surface the backend's actual `detail` for 4xx errors
        // so users see "Audio file is too small..." instead of the misleading
        // "Network error" (provider report: nurses got stuck in retry loop
        // with 100-byte zombie blobs that always returned 400).
        const status = lastError?.response?.status;
        const backendDetail = lastError?.response?.data?.detail;
        let errorMsg;
        if (status === 429) {
          errorMsg = 'Rate limited. Wait a moment and try "Enhance with AI".';
        } else if (lastError?.code === 'ECONNABORTED') {
          errorMsg = 'Timed out. Click "Enhance with AI" to retry.';
        } else if (status === 400 && typeof backendDetail === 'string' && backendDetail) {
          // Backend gave us a specific reason — e.g. "Audio file is too small
          // - no audio was captured. Check your microphone." Show it directly.
          errorMsg = backendDetail;
        } else if (status && status >= 500) {
          errorMsg = `Server error (${status}). Click "Retry" — usually resolves on the second try.`;
        } else {
          errorMsg = 'Network error. Your notes are saved — click "Retry" to try again.';
        }
        setTranscriptionError(errorMsg);
        toast.error(errorMsg, { duration: 8000 });
        setIsTranscribing(false);
        return;
      }
      
      // For files >20MB: decode and split into proper time-based WAV segments
      toast.info(`Large file (${sizeMB}MB) — splitting into segments...`, { duration: 4000 });
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      const SEGMENT_DURATION = 120; // 2 min per segment
      const segmentCount = Math.ceil(audioBuffer.duration / SEGMENT_DURATION);
      let fullTranscription = '';
      let successCount = 0;
      
      for (let i = 0; i < segmentCount; i++) {
        const startSample = Math.floor(i * SEGMENT_DURATION * audioBuffer.sampleRate);
        const endSample = Math.min(Math.floor((i + 1) * SEGMENT_DURATION * audioBuffer.sampleRate), audioBuffer.length);
        const len = endSample - startSample;
        const segBuf = audioContext.createBuffer(audioBuffer.numberOfChannels, len, audioBuffer.sampleRate);
        for (let ch = 0; ch < audioBuffer.numberOfChannels; ch++) {
          const src = audioBuffer.getChannelData(ch);
          const dst = segBuf.getChannelData(ch);
          for (let s = 0; s < len; s++) dst[s] = src[startSample + s];
        }
        const wavBlob = audioBufferToWav(segBuf);
        toast.info(`Transcribing segment ${i + 1}/${segmentCount}...`, { duration: 2000 });
        
        const fd = new FormData();
        fd.append('file', wavBlob, `segment_${i}.wav`);
        fd.append('recording_mode', recordingModeRef.current || 'solo');
        let ok = false;
        for (let attempt = 0; attempt < 3 && !ok; attempt++) {
          try {
            if (attempt > 0) await new Promise(r => setTimeout(r, 2000 * attempt));
            const res = await axios.post(`${API}/api/transcribe`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
            if (res.data.text && !isHallucination(res.data.text)) {
              fullTranscription += (fullTranscription ? ' ' : '') + res.data.text;
              successCount++;
              ok = true;
            }
          } catch (e) { console.error(`Seg ${i+1} attempt ${attempt+1}:`, e.message); }
        }
        if (!ok) toast.error(`Segment ${i+1} failed.`);
      }
      audioContext.close();
      
      if (fullTranscription.trim()) {
        const cleaned = cleanupTranscription(fullTranscription);
        const now = Date.now();
        const lastAt = lastTranscriptionAtRef.current;
        lastTranscriptionAtRef.current = now;
        setNotes(prev => appendWithSessionDivider({ prev, transcribedText: cleaned, now, lastAt }));
        setLastFailedAudio(null); setTranscriptionError(null);
        clearAudioBlob().catch(() => {});
        toast.success(`Transcription complete! (${successCount}/${segmentCount} segments)`);
      } else {
        setLastFailedAudio(audioBlob);
        setTranscriptionError('No speech detected.');
      }
    } catch (error) {
      console.error('Transcription error:', error);
      setLastFailedAudio(audioBlob);
      setTranscriptionError('Transcription failed. Please try again.');
      toast.error('Transcription failed.');
    } finally {
      setIsTranscribing(false);
    }
  }, [cleanupTranscription, isHallucination, quickNotes, setNotes, audioBufferToWav]);

  // Keep transcribeChunkedRef current to avoid stale closures in onstop handler
  useEffect(() => {
    transcribeChunkedRef.current = transcribeAudioChunked;
  }, [transcribeAudioChunked]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      isRecordingRef.current = false;
      clearInterval(recordingIntervalRef.current);
      // Clear time-based appointment alerts
      if (timeReminderIntervalRef.current) {
        clearInterval(timeReminderIntervalRef.current);
        timeReminderIntervalRef.current = null;
      }
      toast.dismiss('time-warning');
      timeAlertFiredRef.current = { gentle: false, urgent: false };
      timeAlertSnoozedRef.current = false;
      
      if (audioLevelIntervalRef.current) {
        clearInterval(audioLevelIntervalRef.current);
        audioLevelIntervalRef.current = null;
      }
      if (audioAnalyserRef.current?.audioContext) {
        try {
          audioAnalyserRef.current.audioContext.close();
        } catch (e) { console.warn('AudioContext close error:', e.message); }
      }
      setAudioLevel(0);
      
      if (speechRecognitionRef.current) {
        try {
          speechRecognitionRef.current.stop();
        } catch (e) { console.warn('SpeechRecognition stop error:', e.message); }
        speechRecognitionRef.current = null;
      }
      
      setIsPaused(false);
    }
  }, [isRecording]);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      if (notesTextareaRef.current) {
        cursorPositionRef.current = {
          start: notesTextareaRef.current.selectionStart || 0,
          end: notesTextareaRef.current.selectionEnd || 0
        };
      } else {
        cursorPositionRef.current = { start: notes.length, end: notes.length };
      }

      // Feb 28, 2026 — pre-flight mic permission check (provider report:
      // nurses denied mic permission, then recordings produced 100-byte
      // empty WebM blobs that kept failing transcribe with "Network error").
      // Catching this upfront with a clear, actionable message saves the
      // user from a confusing retry loop.
      //
      // The Permissions API isn't supported on all browsers (older Safari,
      // some Android WebViews), so we treat any failure as "unknown" and
      // proceed to getUserMedia (which has its own error handling below).
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const status = await navigator.permissions.query({ name: 'microphone' });
          if (status.state === 'denied') {
            const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '');
            const hint = isMobile
              ? 'Open browser settings → Site Settings → Microphone → allow for this site, then reload.'
              : 'Tap the 🔒 (or 🎤) icon in your address bar, choose "Allow microphone", then reload the page.';
            toast.error(`Microphone access is blocked for WriteRight. ${hint}`, {
              id: 'mic-permission-blocked',
              duration: 12000,
            });
            return; // abort start — don't try to record into a void
          }
        }
      } catch (_permErr) {
        // Permissions API unavailable — fall through to getUserMedia.
      }

      // Also check for the secure context that getUserMedia requires (HTTP
      // pages can't access the mic). This is a sanity guard — production is
      // HTTPS but local-IP / file:// would fail silently otherwise.
      if (typeof window !== 'undefined' && window.isSecureContext === false) {
        toast.error('Microphone requires HTTPS. Open WriteRight from https:// (not http:// or file://).', {
          id: 'mic-permission-blocked',
          duration: 12000,
        });
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Monitor for audio track ending (Bluetooth disconnect, etc.)
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.onended = () => {
          console.warn('Audio track ended unexpectedly');
          toast.error('🎤 Microphone disconnected! Recording stopped. Your backup transcript is preserved.', { duration: 6000 });
          stopRecording();
        };
        audioTrack.onmute = () => {
          console.warn('Audio track muted');
          toast.warning('🔇 Microphone muted or disconnected.', { duration: 4000 });
        };
        audioTrack.onunmute = () => {
          console.log('Audio track unmuted');
          toast.info('🎤 Microphone reconnected.', { duration: 2000 });
        };
      }
      
      // Set up audio level monitoring
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(stream);
        microphone.connect(analyser);
        analyser.fftSize = 256;
        audioAnalyserRef.current = { audioContext, analyser };
        
        setNoAudioWarningShown(false);
        setAudioLevel(0);
        
        let noAudioSeconds = 0;
        audioLevelIntervalRef.current = setInterval(() => {
          const dataArray = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
          const level = Math.min(100, Math.round((average / 128) * 100));
          setAudioLevel(level);
          
          if (level < 5) {
            noAudioSeconds += 0.5;
            if (noAudioSeconds >= 8) {
              setNoAudioWarningShown(prev => {
                if (!prev) {
                  toast.warning('🔇 No audio detected! Check your microphone.', { duration: 6000 });
                  return true;
                }
                return prev;
              });
            }
          } else {
            noAudioSeconds = 0;
            setNoAudioWarningShown(false); // Always clear when good audio detected
          }
        }, 500);
      } catch (audioErr) {
        console.log('Audio monitoring not available:', audioErr);
      }
      
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) mimeType = 'audio/mp4';
        else if (MediaRecorder.isTypeSupported('audio/ogg')) mimeType = 'audio/ogg';
        else mimeType = '';
      }
      const mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      hasPlayed21MBRef.current = false;
      hasPlayed23MBRef.current = false;
      setRecordingSize(0);
      setRecordingDuration(0);
      setHasPlayed21MBWarning(false);
      setHasPlayed23MBWarning(false);
      setQuickNotes('');
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          const currentSize = audioChunksRef.current.reduce((acc, chunk) => acc + chunk.size, 0);
          setRecordingSize(currentSize);
          
          // Auto-save to IndexedDB every ~10 chunks (~10 seconds) so a mid-recording 
          // deploy/refresh doesn't lose the audio
          if (audioChunksRef.current.length % 10 === 0) {
            const partialBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType || 'audio/webm' });
            saveAudioBlob(partialBlob, { 
              mimeType: partialBlob.type, 
              sizeMB: (partialBlob.size / (1024*1024)).toFixed(2),
              partial: true 
            }).catch(() => {});
          }
          
          if (currentSize >= MAX_FILE_SIZE) {
            playAutoStopAlert();
            if (reminderIntervalRef.current) { clearInterval(reminderIntervalRef.current); reminderIntervalRef.current = null; }
            toast.error('Auto-stopping at max size to prevent data loss. Transcribing now...', { duration: 6000 });
            stopRecording();
          } else if (currentSize >= WARNING_SIZE_MEDIUM && !hasPlayed23MBRef.current) {
            hasPlayed23MBRef.current = true;
            setHasPlayed23MBWarning(true);
            snoozedRef.current = false;
            playMediumWarningChime();
            toast.warning('14MB — wrap up soon! Auto-stop at 24MB.', { 
              id: 'size-warning',
              duration: Infinity,
              action: { label: 'Got it', onClick: snoozeWarning }
            });
            startReminderBeeps('medium');
          } else if (currentSize >= WARNING_SIZE_GENTLE && !hasPlayed21MBRef.current) {
            hasPlayed21MBRef.current = true;
            setHasPlayed21MBWarning(true);
            snoozedRef.current = false;
            playGentleChime();
            toast.info('12MB — consider wrapping up to stay within upload limits.', { 
              id: 'size-warning',
              duration: Infinity,
              action: { label: 'Got it', onClick: snoozeWarning }
            });
            startReminderBeeps('gentle');
          }
        }
      };
      
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        clearInterval(recordingIntervalRef.current);
        if (reminderIntervalRef.current) { clearInterval(reminderIntervalRef.current); reminderIntervalRef.current = null; }
        toast.dismiss('size-warning');
        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType || 'audio/webm' });

        // Feb 28, 2026 — zombie-blob guard (provider report: nurses repeatedly
        // hit "Network error" because 100-byte empty MediaRecorder blobs from
        // a denied mic or instant-stop tap were being saved to IndexedDB,
        // then auto-restored on reload, then re-uploaded → 400 "too small".
        // Backend MIN_BYTES is 1024 (see routes/transcribe.py L58). We mirror
        // that here so tiny blobs never enter the retry/recovery pipeline.
        const MIN_VALID_AUDIO_BYTES = 1024;
        if (audioBlob.size < MIN_VALID_AUDIO_BYTES) {
          // Don't set lastFailedAudio (no zombie "Transcribe Now" banner).
          // Don't save to IndexedDB (clear any prior to be safe).
          clearAudioBlob().catch(() => {});
        } else {
          // Save the audio blob for retry if needed
          setLastFailedAudio(audioBlob);
          // Persist to IndexedDB so it survives page refreshes/deploys
          saveAudioBlob(audioBlob, { mimeType: audioBlob.type, sizeMB: (audioBlob.size / (1024*1024)).toFixed(2) })
            .catch(() => {}); // silent fail — in-memory copy still available
        }
        
        // Keep browser SpeechRecognition text as fallback
        // Only clear it if Whisper succeeds later (handled in transcribeAudioChunked)
        const browserCharsAdded = browserSRCharCountRef.current;
        browserSRCharCountRef.current = 0;
        
        // Safety net: backup current notes to localStorage
        try {
          const currentNotes = document.querySelector('[data-testid="notes-textarea"]')?.value || '';
          if (currentNotes.trim()) {
            localStorage.setItem('writeright_autosave', JSON.stringify({ notes: currentNotes, savedAt: new Date().toISOString(), source: 'recording_stop' }));
          }
        } catch (e) { /* silent */ }
        
        setRecordingSize(0);
        setRecordingDuration(0);
        
        // AUTO-SEND to Whisper for authoritative transcription
        if (audioBlob.size > 1000) {
          if (browserCharsAdded > 0) {
            toast.info('Live text captured. Enhancing with Whisper...', { duration: 3000 });
          } else {
            toast.info('Sending to Whisper for transcription...', { duration: 3000 });
          }
          if (transcribeChunkedRef.current) {
            transcribeChunkedRef.current(audioBlob);
          }
        } else if (browserCharsAdded > 0) {
          toast.success('Live transcription captured.', { duration: 3000 });
        } else {
          toast.warning('Recording too short. Please try again.', { duration: 3000 });
        }
      };
      
      // Handle recording errors - this catches glitches/interruptions
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        // Check if recorder is actually dead after the error
        if (mediaRecorder.state === 'inactive') {
          playAutoStopAlert();
          toast.error('Recording stopped due to an error. Audio saved — click "Retry" to transcribe.', { duration: 8000 });
          stopRecording();
        } else {
          toast.error(`Recording error: ${event.error?.name || 'Unknown'}. Your backup transcript is preserved.`, { duration: 5000 });
        }
      };
      
      // Handle when recording becomes inactive unexpectedly
      mediaRecorder.onpause = () => {
        console.log('MediaRecorder paused unexpectedly');
        // Only show warning if we didn't intentionally pause
        if (!isPaused) {
          toast.warning('Recording paused unexpectedly. Check your microphone connection.', { duration: 4000 });
        }
      };
      
      mediaRecorder.start(1000);
      setIsRecording(true);
      isRecordingRef.current = true;
      setLiveTranscript('');
      
      // Start browser speech recognition - live preview only, Whisper is authoritative
      try {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
          const recognition = new SpeechRecognition();
          recognition.continuous = true;
          recognition.interimResults = true;
          recognition.lang = 'en-US';
          
          // Track what we've already added to notes to avoid duplicates
          let lastFinalizedIndex = 0;
          browserSRCharCountRef.current = 0; // Reset char counter
          
          recognition.onresult = (event) => {
            let interimTranscript = '';
            let finalTranscript = '';
            
            // Separate final vs interim results
            for (let i = lastFinalizedIndex; i < event.results.length; i++) {
              const result = event.results[i];
              if (result.isFinal) {
                finalTranscript += result[0].transcript + ' ';
                lastFinalizedIndex = i + 1;
              } else {
                interimTranscript += result[0].transcript;
              }
            }
            
            // Update live transcript display (shows interim text)
            setLiveTranscript(interimTranscript);
            // Mirror to a window-scoped ref so the visibility-change
            // handler (in the global effect above) can read the latest
            // interim text WITHOUT being recreated every keystroke.
            // Mobile fix Feb 28, 2026 — see useEffect with `pagehide`.
            try { window.__lastLiveTranscript = interimTranscript; } catch { /* ignore */ }
            
            // Append FINAL transcript to the end of notes (live preview)
            if (finalTranscript.trim()) {
              browserSRCharCountRef.current += finalTranscript.length;
              setNotes(prev => {
                return prev + finalTranscript;
              });
            }
          };
          
          recognition.onerror = (event) => {
            if (event.error !== 'no-speech' && event.error !== 'aborted') {
              console.warn('Speech recognition error:', event.error);
            }
          };
          
          // Auto-restart if it stops unexpectedly
          recognition.onend = () => {
            if (isRecordingRef.current && speechRecognitionRef.current) {
              try {
                recognition.start();
              } catch (e) {
                console.log('Could not restart speech recognition:', e);
              }
            }
          };
          
          recognition.start();
          speechRecognitionRef.current = recognition;
          setUseLiveTranscription(true);
          toast.success('Recording — text appears live, Whisper transcribes on stop', { duration: 3000 });
        }
      } catch (err) {
        console.log('Browser speech recognition not available:', err);
        toast.warning('Live transcription not available in this browser. Recording will use Whisper API.', { duration: 4000 });
      }
      
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => {
          const elapsed = prev + 1;
          // Time-based appointment alerts
          if (elapsed === 720 && !timeAlertFiredRef.current.gentle) { // 12 minutes
            timeAlertFiredRef.current.gentle = true;
            playGentleChime();
            toast.warning('12 min — consider wrapping up this visit.', {
              id: 'time-warning',
              duration: Infinity,
              action: { label: 'Got it', onClick: snoozeTimeAlert }
            });
            startTimeReminderBeeps('gentle');
          } else if (elapsed === 900 && !timeAlertFiredRef.current.urgent) { // 15 minutes
            timeAlertFiredRef.current.urgent = true;
            timeAlertSnoozedRef.current = false;
            playMediumWarningChime();
            toast.warning('15 min — time to wrap up!', {
              id: 'time-warning',
              duration: Infinity,
              action: { label: 'Got it', onClick: snoozeTimeAlert }
            });
            startTimeReminderBeeps('urgent');
          }
          return elapsed;
        });
        
        // Watchdog: detect if MediaRecorder silently died
        const recorder = mediaRecorderRef.current;
        if (recorder && recorder.state === 'inactive' && isRecordingRef.current) {
          console.error('Watchdog: MediaRecorder went inactive while recording was active');
          playAutoStopAlert();
          toast.error('Recording stopped unexpectedly! Audio saved — click "Retry" to transcribe.', { duration: 10000 });
          stopRecording();
        }
      }, 1000);
      
    } catch (error) {
      console.error('Microphone access error:', error);
      // Feb 28, 2026 — actionable error messages for each failure mode.
      // Previously the user got a single short toast ("Microphone access
      // denied") that didn't tell them HOW to fix it.
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '');
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        const hint = isMobile
          ? 'Open browser settings → Site Settings → Microphone → allow for this site, then reload.'
          : 'Tap the 🔒 (or 🎤) icon in your address bar, choose "Allow microphone", then reload.';
        toast.error(`Microphone access denied. ${hint}`, { id: 'mic-permission-blocked', duration: 12000 });
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error('No microphone found. Plug in a mic / headset, or check your audio device settings.', { duration: 8000 });
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        toast.error('Microphone is in use by another app (Zoom, Teams, etc.). Close it and try again.', { duration: 8000 });
      } else if (error.name === 'OverconstrainedError') {
        toast.error('Microphone does not meet the required constraints. Try a different mic.', { duration: 6000 });
      } else if (error.name === 'SecurityError') {
        toast.error('Microphone blocked by browser security. Make sure the page is loaded over HTTPS.', { duration: 8000 });
      } else {
        toast.error(`Could not access microphone: ${error.message || error.name || 'unknown error'}`, { duration: 6000 });
      }
    }
  }, [notes, playAutoStopAlert, playGentleChime, playMediumWarningChime, stopRecording, isPaused, setNotes, snoozeWarning, startReminderBeeps, snoozeTimeAlert, startTimeReminderBeeps]);

  // Pause recording
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
        clearInterval(recordingIntervalRef.current);
        
        if (speechRecognitionRef.current) {
          try {
            speechRecognitionRef.current.stop();
          } catch (e) { console.warn('SpeechRecognition pause stop error:', e.message); }
        }
        
        toast.info('Recording paused', { duration: 2000 });
      }
    }
  }, [isRecording, isPaused]);

  // Resume recording
  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      if (mediaRecorderRef.current.state === 'paused') {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
        
        recordingIntervalRef.current = setInterval(() => {
          setRecordingDuration(prev => prev + 1);
        }, 1000);
        
        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
          try {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'en-US';
            
            recognition.onresult = (event) => {
              let transcript = '';
              for (let i = 0; i < event.results.length; i++) {
                transcript += event.results[i][0].transcript;
              }
              setLiveTranscript(transcript);
            };
            
            recognition.onerror = (e) => { console.warn('SpeechRecognition resume error:', e.error); };
            recognition.start();
            speechRecognitionRef.current = recognition;
          } catch (e) { console.warn('SpeechRecognition resume init error:', e.message); }
        }
        
        toast.success('▶️ Recording resumed', { duration: 2000 });
      }
    }
  }, [isRecording, isPaused]);

  // Retry transcription — always use chunked for safety (avoids proxy body size limits)
  const retryTranscription = useCallback(() => {
    if (!lastFailedAudio) return;
    // Feb 28, 2026 — zombie-blob guard. Backend rejects <1024 bytes; surface
    // a clear "mic captured nothing" error instead of retrying-into-a-loop.
    const MIN_VALID_AUDIO_BYTES = 1024;
    if (lastFailedAudio.size < MIN_VALID_AUDIO_BYTES) {
      toast.error('Saved recording is empty — your microphone may have been muted or denied. Please record again.', { duration: 5000 });
      setLastFailedAudio(null);
      setTranscriptionError(null);
      clearAudioBlob().catch(() => {});
      return;
    }
    toast.info(`Processing audio (${(lastFailedAudio.size / (1024 * 1024)).toFixed(1)}MB)...`, { duration: 4000 });
    transcribeAudioChunked(lastFailedAudio);
  }, [lastFailedAudio, transcribeAudioChunked]);

  // Clear transcription error
  const clearTranscriptionError = useCallback(() => {
    setTranscriptionError(null);
    setLastFailedAudio(null);
  }, []);

  // Enhance notes with AI (Whisper) - optional cleanup of live transcription
  const enhanceWithAI = useCallback(async () => {
    if (!lastFailedAudio) {
      toast.info('No recording available to enhance. Record something first.', { duration: 3000 });
      return;
    }
    
    // Feb 28, 2026 — strengthened from `size === 0` to `< 1024 bytes` to
    // match backend MIN_BYTES (routes/transcribe.py L58). Catches empty
    // WebM container blobs (~100 bytes) that result from denied mic
    // permission or tap-and-immediately-stop interactions.
    const MIN_VALID_AUDIO_BYTES = 1024;
    if (!(lastFailedAudio instanceof Blob) || lastFailedAudio.size < MIN_VALID_AUDIO_BYTES) {
      toast.error('Recording is empty (microphone may have been muted or denied permission). Please record again.', { duration: 5000 });
      Sentry.captureMessage('EnhanceWithAI: Empty/tiny audio blob', {
        level: 'warning',
        tags: { feature: 'transcription' },
        extra: { 
          is_blob: lastFailedAudio instanceof Blob,
          size: lastFailedAudio?.size 
        }
      });
      setLastFailedAudio(null);
      clearAudioBlob().catch(() => {});
      return;
    }
    
    console.log('[EnhanceWithAI] Starting enhancement, blob size:', lastFailedAudio.size, 'type:', lastFailedAudio.type);
    toast.info('Sending to AI for enhanced transcription...', { duration: 2000 });
    
    try {
      // Always use chunked transcription for deployment safety (proxy body size limits)
      await transcribeAudioChunked(lastFailedAudio, true);
    } catch (error) {
      console.error('[EnhanceWithAI] Error:', error);
      Sentry.captureException(error, {
        tags: { feature: 'enhance_with_ai' },
        extra: { 
          blob_size: lastFailedAudio?.size,
          blob_type: lastFailedAudio?.type,
          retry_count: retryCount
        }
      });
      // Error is already handled in transcribeAudio/transcribeAudioChunked
    }
  }, [lastFailedAudio, transcribeAudioChunked, retryCount]);

  // Use browser backup transcription
  const useBrowserBackup = useCallback(() => {
    if (liveTranscript.trim()) {
      setNotes(prev => prev ? `${prev}\n\n${liveTranscript}` : liveTranscript);
      setLiveTranscript('');
      setLastFailedAudio(null);
      setTranscriptionError(null);
      toast.success('Browser backup transcription used');
    } else {
      toast.error('No browser backup transcription available');
    }
  }, [liveTranscript, setNotes]);

  return {
    // State
    isRecording,
    isTranscribing,
    screenLockActive,
    recordingSize,
    recordingDuration,
    liveTranscript,
    useLiveTranscription,
    quickNotes,
    setQuickNotes,
    audioLevel,
    noAudioWarningShown,
    isPaused,
    lastFailedAudio,
    transcriptionError,
    // Recording mode (solo / in_person / phone) — Deepgram diarization control
    recordingMode,
    setRecordingMode,
    
    // Refs
    notesTextareaRef,
    
    // Actions
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    retryTranscription,
    clearTranscriptionError,
    useBrowserBackup,
    enhanceWithAI,
    // Force-cancel: clears all stuck recording/transcribing state so the
    // FloatingDictation ribbon can never get permanently stuck on screen
    // (fixes May 7, 2026 report: "audio not recording ribbon stays there
    // for a long time, you can't exit it, it blocks Next Patient").
    forceCancel: () => {
      try { if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop(); } catch { /* ignore */ }
      try { if (speechRecognitionRef.current) speechRecognitionRef.current.stop(); } catch { /* ignore */ }
      if (recordingIntervalRef.current) { clearInterval(recordingIntervalRef.current); recordingIntervalRef.current = null; }
      if (audioLevelIntervalRef.current) { clearInterval(audioLevelIntervalRef.current); audioLevelIntervalRef.current = null; }
      isRecordingRef.current = false;
      setIsRecording(false);
      setIsTranscribing(false);
      setIsPaused(false);
      setAudioLevel(0);
      setRecordingDuration(0);
      setRecordingSize(0);
      audioChunksRef.current = [];
    },
    
    // Computed
    recordingSizeMB: (recordingSize / (1024 * 1024)).toFixed(1),
    recordingDurationFormatted: `${Math.floor(recordingDuration / 60)}:${(recordingDuration % 60).toString().padStart(2, '0')}`,
  };
};

export default useRecording;
